/**
 * Centralized configuration for inspector tools
 * Defines tool routing and metadata
 */
import { generateInspectorUrl } from '../utils/urls'

export interface ToolRoute {
  id: string
  path: string
  hasPage: boolean
  topLevel?: boolean
}

/**
 * Tool route definitions
 * Tools with hasPage=true navigate to dedicated pages
 * Tools with hasPage=false use the API executor modal
 * Tools with topLevel=true route to /inspect/{fp}/{path} rather than /inspect/{fp}/tools/{path}
 */
export const TOOL_ROUTES: Record<string, ToolRoute> = {
  invoice_payments_export: {
    id: 'invoice_payments_export',
    path: 'export-payments',
    hasPage: false  // Direct download, no page needed
  },

  duplicate_sales: {
    id: 'duplicate_sales',
    path: 'duplicate-sales',
    hasPage: true
  },

  cogs_review: {
    id: 'cogs_review',
    path: 'cogs-review',
    hasPage: true
  },

  inventory_balance_review: {
    id: 'inventory_balance_review',
    path: 'inventory-balance',
    hasPage: true
  },

  product_catalogue_review: {
    id: 'product_catalogue_review',
    path: 'product-catalogue-review',
    hasPage: true
  },

  health_check: {
    id: 'health_check',
    path: 'health',
    hasPage: true,
    topLevel: true
  }
}

/**
 * Generate the full tool URL for a given tool and franchise
 * @param fpCode - The franchise partner code
 * @param toolId - The tool identifier
 * @param id - Optional `franchise_partners.id` to emit the tenure-pinned form
 * @returns The full tool URL path
 */
export function generateToolUrl(
  fpCode: string,
  toolId: string,
  id?: number | null,
): string {
  const tool = TOOL_ROUTES[toolId]
  if (!tool) {
    console.warn(`Tool ${toolId} not found in TOOL_ROUTES`)
    return generateInspectorUrl(fpCode, { id, subpath: 'tools' })
  }

  const subpath = tool.topLevel ? tool.path : `tools/${tool.path}`
  return generateInspectorUrl(fpCode, { id, subpath })
}

/**
 * Check if a tool has a dedicated page implementation
 * @param toolId - The tool identifier
 * @returns True if the tool has a page, false if it uses the API executor
 */
export function toolHasPage(toolId: string): boolean {
  return TOOL_ROUTES[toolId]?.hasPage || false
}
